/* ==========================================================================
   Vanguarda — product page behaviour.

   Four additions, all made from the outside so nothing large has to be
   rewritten:

     1. COLOUR -> PHOTO. theme.js already reacts to a colour change, but with
        gallery.prepend(), a real DOM move: pick three colours and the
        merchant's photo order is gone for the rest of the visit. Here the
        order is restored first, then the chosen colour's photo is brought to
        the front — by scrolling the carousel on a phone and by CSS order on a
        desktop, so the strip never slides sideways under a thumb — and the
        photo that arrives fades in, so the change is seen rather than guessed
        at. Which photo belongs to which colour is read from the variant's own
        media, then from its older featured_image, then from the alt text or
        the file name. A product whose variants carry no photo and whose files
        never name a colour has no such record, and this does nothing for it:
        showing a garment in a colour the shopper did not pick is worse than
        showing the one they were already looking at.

     2. SIZE -> MEASUREMENTS, live. The numbers are read out of the size guide
        drawer the page already renders, so they are the table this product
        got (tops, bottoms, waist or footwear, decided server-side from its own
        tags and option values) and not one table for the whole catalogue.

     3. GALLERY DOTS on a phone, so full-bleed slides still say how many photos
        there are and which one you are on.

     4. BUY IT NOW beside Add to bag, for the shopper who wants one piece and
        the checkout. Add to bag keeps its own behaviour, drawer included.

   It degrades quietly: every element it touches either already exists or is
   one it created, so if the file fails to load the page is what it was.
   ========================================================================== */
(function () {
  'use strict';

  var PHONE = '(max-width: 749px)';
  var unit = 'in';

  function isPhone() { return window.matchMedia(PHONE).matches; }
  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  function txt(el) { return el ? String(el.textContent).replace(/\s+/g, ' ').trim() : ''; }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* styles. Injected, not shipped in a stylesheet: every element these rules
     describe is created by this file, so without the file there is nothing to
     style. The gallery's own sizing is inline in snippets/meta-tags.liquid
     instead, because that has to be right at first paint. */
  function styleOnce() {
    if (document.getElementById('vg-pdp-css')) return;
    var st = document.createElement('style');
    st.id = 'vg-pdp-css';
    st.textContent = [
      /* the live measurements under the size buttons */
      '.pmeasure{margin-top:12px;border:1px solid var(--vg-line);padding:11px 13px}',
      '.pmeasure__head{display:flex;align-items:baseline;gap:8px;font-size:0.75rem;',
      'letter-spacing:0.06em;text-transform:uppercase;color:var(--vg-muted)}',
      '.pmeasure__head strong{color:var(--vg-text);font-weight:600}',
      '.pmeasure__unit{margin-left:auto;flex:none;border:1px solid var(--vg-line);',
      'background:transparent;color:var(--vg-muted);font:inherit;font-size:0.6875rem;',
      'letter-spacing:0.08em;text-transform:uppercase;padding:3px 9px;min-height:26px;cursor:pointer}',
      '.pmeasure__unit:hover{border-color:var(--vg-text);color:var(--vg-text)}',
      '.pmeasure__list{display:flex;flex-wrap:wrap;gap:6px 22px;margin-top:9px}',
      '.pmeasure__list>div{display:flex;flex-direction:column;gap:2px}',
      '.pmeasure__list dt{font-size:0.6875rem;letter-spacing:0.08em;text-transform:uppercase;',
      'color:var(--vg-muted)}',
      '.pmeasure__list dd{font-size:0.875rem;font-variant-numeric:tabular-nums}',
      '.pmeasure__empty{font-size:0.8125rem;color:var(--vg-muted)}',
      '.pmeasure__note{margin-top:9px;font-size:0.6875rem;line-height:1.45;color:var(--vg-muted)}',

      /* buy it now */
      '.pbuy-now{width:100%;margin-top:10px}',
      '.pbuy-err{margin-top:8px;font-size:0.75rem;color:var(--vg-accent)}',

      /* gallery dots, phone only */
      '.pgal-dots{display:none}',
      '@media (max-width:749px){',
      '.pgal-dots{display:flex;justify-content:center;gap:7px;padding:12px 0 2px}',
      '.pgal-dots button{width:7px;height:7px;padding:0;border:0;border-radius:50%;',
      'background:var(--vg-line);cursor:pointer;transition:background 160ms var(--vg-ease,ease),',
      'transform 160ms var(--vg-ease,ease)}',
      '.pgal-dots button[aria-current="true"]{background:var(--vg-text);transform:scale(1.35)}',
      '}',
      '@media (prefers-reduced-motion:reduce){.pgal-dots button{transition:none}}',

      /* the photo brought to the front on a colour change */
      '@keyframes vgMediaIn{from{opacity:0;transform:scale(1.015)}to{opacity:1;transform:none}}',
      '.product__gallery>.vg-media-in{animation:vgMediaIn 380ms cubic-bezier(.2,.6,.2,1) both}',
      '@media (prefers-reduced-motion:reduce){',
      '.product__gallery>.vg-media-in{animation:none}',
      '}'
    ].join('');
    document.head.appendChild(st);
  }

  /* ----------------------------------------------------------------- gallery */
  var gallery = null;
  var order = [];
  var dots = null;
  var dotBtns = [];
  var promoted = null;

  function galleryItems() { return order; }

  function initGallery() {
    gallery = $('.product__gallery');
    if (!gallery) return;
    order = $$(':scope > *', gallery);
    if (order.length < 2) return;
    buildDots();
  }

  function buildDots() {
    dots = document.createElement('div');
    dots.className = 'pgal-dots';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Product photos');
    dotBtns = [];
    order.forEach(function (el, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + order.length);
      b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      b.addEventListener('click', function () { scrollTo_(el); });
      dots.appendChild(b);
      dotBtns.push(b);
    });
    gallery.parentNode.insertBefore(dots, gallery.nextSibling);

    var queued = false;
    gallery.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; markDots(); });
    }, { passive: true });
  }

  /* The slide under the eye is the one whose centre is nearest the carousel's
     centre — steadier than an observer per slide, and never "none" mid-swipe. */
  function markDots() {
    if (!dots || !gallery) return;
    var mid = gallery.scrollLeft + gallery.clientWidth / 2;
    var best = 0;
    var bestD = Infinity;
    var visual = visualOrder();
    for (var i = 0; i < visual.length; i++) {
      var el = visual[i];
      var c = el.offsetLeft - gallery.offsetLeft + el.offsetWidth / 2;
      var d = Math.abs(c - mid);
      if (d < bestD) { bestD = d; best = i; }
    }
    /* Hidden slides have hidden dots, so the two lists stay the same length
       and index i of one is index i of the other. */
    $$('button', dots).filter(function (b) { return !b.hidden; })
      .forEach(function (b, i) {
        b.setAttribute('aria-current', i === best ? 'true' : 'false');
      });
  }

  function visualOrder() {
    return $$(':scope > *', gallery).filter(function (el) { return !el.hidden; });
  }

  function scrollTo_(el) {
    if (!gallery || !el) return;
    var left = el.offsetLeft - gallery.offsetLeft;
    try { gallery.scrollTo({ left: left, behavior: 'smooth' }); }
    catch (err) { gallery.scrollLeft = left; }
  }

  /* 25/10 — a cor trocava a foto e a PAGINA ficava parada.

     No celular os botoes de cor nascem abaixo da dobra: a galeria ocupa 62vh
     no topo, depois vem titulo, preco, e so entao as variantes. Ou seja, o
     cliente escolhe a cor com a galeria JA FORA DA TELA. A foto certa aparecia
     num lugar que ele nao estava olhando, e ele tinha de subir no dedo toda
     vez para ver o que escolheu. Quem nao sobe, escolhe no escuro — e tem bom
     motivo para desistir.

     scrollTo_ acima resolve o eixo horizontal (move a tira ate o slide certo).
     Esta funcao resolve o vertical: sobe a pagina ate encostar o topo da
     galeria logo abaixo do cabecalho.

     So mexe quando a galeria NAO esta inteira visivel. Rolar a pagina sem
     necessidade e pior do que nao rolar: tira o dedo do lugar onde a pessoa
     estava trabalhando, e ela perde a linha dos tamanhos que acabou de ler.

     A altura do cabecalho e MEDIDA do elemento real, nao lida de
     --vg-header-h: aquela variavel e um valor fixo de 74px no base.css, e a
     altura real muda com a barra de anuncio ligada ou desligada. O .header e
     `position: sticky; top: 0`, entao ele cobre o topo da tela e o espaco
     dele precisa ser descontado. Mesmo quando esta escondido
     (.header--hidden, que e so um transform), reservar a altura e o certo:
     rolar para cima traz o cabecalho de volta, e sem a reserva ele cairia em
     cima da foto. */
  function reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function headerOffset() {
    var h = document.querySelector('.header');
    if (!h) return 0;
    var pos = window.getComputedStyle(h).position;
    if (pos !== 'fixed' && pos !== 'sticky') return 0;
    return h.getBoundingClientRect().height;
  }

  function revealGallery() {
    if (!gallery) return;
    var pad = headerOffset() + 8;
    var box = gallery.getBoundingClientRect();
    if (box.top >= pad && box.bottom <= window.innerHeight) return;
    var y = window.pageYOffset + box.top - pad;
    if (y < 0) y = 0;
    try {
      window.scrollTo({ top: y, behavior: reduceMotion() ? 'auto' : 'smooth' });
    } catch (err) {
      window.scrollTo(0, y);
    }
  }

  /* Undo theme.js's prepend before doing anything else. */
  function restoreOrder() {
    if (!gallery || !order.length) return;
    var kids = gallery.children;
    var same = kids.length === order.length;
    if (same) {
      for (var i = 0; i < order.length; i++) {
        if (kids[i] !== order[i]) { same = false; break; }
      }
    }
    if (same) return;
    for (var j = 0; j < order.length; j++) gallery.appendChild(order[j]);
  }

  function clearPromotion() {
    order.forEach(function (el) {
      el.style.order = '';
      el.classList.remove('vg-media-in');
    });
  }

  /* Remove, force a reflow, re-add: without the reflow the browser never sees
     the class leave, and the animation does not play a second time. */
  function animateIn(el) {
    if (!el) return;
    el.classList.remove('vg-media-in');
    void el.offsetWidth;
    el.classList.add('vg-media-in');
  }

  /* ------------------------------------------------ one colour at a time */

  /* A product sold in three colours carries three sets of photos, and before
     this the shopper scrolled through all of them: pick BLACK and the next
     thing under the thumb was the same garment in oat. The gallery now shows
     only the photos of the colour that is selected.

     Which photo belongs to which colour is read the same way promote() reads
     it — the alt text and the file name — so a photo that names no colour
     (a fabric close-up, a size graphic) is treated as shared and stays
     visible for every colour. And if NO photo names a colour, nothing is
     hidden: a product whose photos carry no such record is left exactly as
     the merchant arranged it, which is the same promise the rest of this
     file makes. */
  function colourValues() {
    var rows = optionRows();
    for (var i = 0; i < rows.length; i++) {
      var name = rowLabel(rows[i]);
      if (name.indexOf('color') > -1 || name.indexOf('colour') > -1) {
        return $$('input', rows[i]).map(function (el) { return el.value; });
      }
    }
    return [];
  }

  function colourOf(slide, values) {
    var hay = haystack(slide);
    for (var i = 0; i < values.length; i++) {
      var want = ' ' + String(values[i]).toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
      if (want !== '  ' && hay.indexOf(want) > -1) return values[i];
    }
    return null;
  }

  function filterColour(colour) {
    if (!gallery || !order.length) return;
    /* Longest name first, so "Light Blue" is never answered by "Blue". */
    var values = colourValues().sort(function (a, b) { return b.length - a.length; });
    var owner = [];
    var tagged = 0;
    var i;
    for (i = 0; i < order.length; i++) {
      owner[i] = values.length > 1 ? colourOf(order[i], values) : null;
      if (owner[i]) tagged++;
    }
    var on = values.length > 1 && tagged > 0 && colour;
    var shown = 0;
    for (i = 0; i < order.length; i++) {
      var hide = !!(on && owner[i] && owner[i] !== colour);
      order[i].hidden = hide;
      order[i].style.display = hide ? 'none' : '';
      if (dotBtns[i]) dotBtns[i].hidden = hide;
      if (!hide) shown++;
    }
    var n = 0;
    for (i = 0; i < order.length; i++) {
      if (order[i].hidden || !dotBtns[i]) continue;
      n++;
      dotBtns[i].setAttribute('aria-label', 'Photo ' + n + ' of ' + shown);
    }
  }

  function promote(el) {
    restoreOrder();
    clearPromotion();
    filterColour(chosenColour());
    if (!el) { promoted = null; markDots(); return; }
    var changed = el !== promoted;
    promoted = el;
    if (isPhone()) {
      /* Scroll to the photo rather than reorder the strip under the thumb. */
      scrollTo_(el);
      /* ...e traz a propria galeria de volta para a tela. So na troca de
         verdade: o botao de tamanho dispara o mesmo evento, e subir a pagina
         a cada clique em tamanho seria insuportavel. */
      if (changed) revealGallery();
    } else {
      el.style.order = '-1';
      /* order:-1 puts it at the top of the column. A shopper who has scrolled
         down to the size buttons would never see that happen, so bring it back
         into view — block:'nearest' moves the page only when it has to. */
      if (changed) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
        catch (err) {}
      }
    }
    /* Only on a real change: the size buttons fire the same event, and a photo
       that flashes every time a size is picked reads as a glitch. */
    if (changed) animateIn(el);
    markDots();
  }

  /* ------------------------------------------------------- variant resolving */
  function picker() { return $('vg-variant-picker'); }

  var variantsCache = null;
  function variants() {
    if (variantsCache) return variantsCache;
    var p = picker();
    var raw = p ? $('script[type="application/json"]', p) : null;
    try { variantsCache = raw ? JSON.parse(raw.textContent) : []; }
    catch (err) { variantsCache = []; }
    return variantsCache;
  }

  function chosenOptions() {
    var p = picker();
    if (!p) return [];
    return $$('input[type="radio"]:checked, select', p).map(function (el) { return el.value; });
  }

  function currentVariant() {
    var opts = chosenOptions();
    if (!opts.length) return null;
    var list = variants();
    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      if (!v.options || v.options.length !== opts.length) continue;
      var ok = true;
      for (var j = 0; j < opts.length; j++) {
        if (String(v.options[j]) !== String(opts[j])) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  /* One slide flattened to searchable words: its alt text and the file name of
     its photo. The file name is in there because part of this catalogue came in
     with the colour in the file and nothing in the alt. */
  function haystack(slide) {
    var img = slide.querySelector('img');
    if (!img) return '';
    var src = img.getAttribute('src') || '';
    try { src = decodeURIComponent(src); } catch (err) {}
    return ' ' + ((img.getAttribute('alt') || '') + ' ' + src)
      .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
  }

  function fileOf(url) {
    return String(url || '').split('?')[0].split('/').pop();
  }

  /* Whole colour name first, so "Chocolate Brown" cannot be answered by the
     brown-trimmed white one. Single words only as a second pass, and only for
     colours written in more than one word. */
  function slideFor(colour) {
    if (!colour) return null;
    var want = ' ' + String(colour).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
    if (want === '  ') return null;
    var i;
    for (i = 0; i < order.length; i++) {
      if (haystack(order[i]).indexOf(want) > -1) return order[i];
    }
    var words = want.trim().split(' ').filter(function (w) { return w.length > 2; });
    if (words.length < 2) return null;
    for (i = 0; i < order.length; i++) {
      var hay = haystack(order[i]);
      for (var j = 0; j < words.length; j++) {
        if (hay.indexOf(' ' + words[j] + ' ') > -1) return order[i];
      }
    }
    return null;
  }

  /* The variant's own photo if it has one: featured_media, then the older
     featured_image some of these variants carry instead. Failing both, the
     first slide whose alt text or file name names the colour.

     Most of this catalogue has no variant photo assigned and no colour in
     either the alt or the file, and for those products this returns nothing on
     purpose. There is no record of which photo is which colour, and a guess
     would show the shopper a garment in a colour they did not choose. */
  function mediaFor(variant) {
    if (!gallery) return null;
    var i;
    if (variant && variant.featured_media && variant.featured_media.id) {
      var hit = gallery.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
      if (hit) return hit;
    }
    if (variant && variant.featured_image && variant.featured_image.src) {
      var file = fileOf(variant.featured_image.src);
      for (i = 0; i < order.length; i++) {
        var img = order[i].querySelector('img');
        if (img && file && fileOf(img.getAttribute('src')) === file) return order[i];
      }
    }
    return slideFor(chosenColour());
  }

  function optionRows() { return $$('.product__info .option'); }

  function rowLabel(row) { return txt($('.option__head .label', row)).toLowerCase(); }

  function chosenColour() {
    var rows = optionRows();
    for (var i = 0; i < rows.length; i++) {
      var name = rowLabel(rows[i]);
      if (name.indexOf('color') > -1 || name.indexOf('colour') > -1) {
        var on = rows[i].querySelector('input:checked');
        return on ? on.value : null;
      }
    }
    return null;
  }

  function sizeRow() {
    var rows = optionRows();
    var i;
    for (i = 0; i < rows.length; i++) {
      if (rows[i].querySelector('.size-chart__trigger')) return rows[i];
    }
    for (i = 0; i < rows.length; i++) {
      if (rowLabel(rows[i]).indexOf('size') > -1) return rows[i];
    }
    return null;
  }

  /* measurements, read from the size guide drawer this page already renders:
     that table was chosen server-side from this product's own tags and option
     values, so the line under the buttons is its chart, not a house average. */
  var chartCache = null;
  function chartTable() {
    if (chartCache !== null) return chartCache;
    var t = $('#SizeChart .sc__table');
    if (!t) { chartCache = false; return chartCache; }
    var isGarment = t.getAttribute('data-kind') === 'garment';
    var cols = $$('thead th', t).map(txt);
    var rows = $$('tbody tr', t).map(function (tr) {
      return {
        size: txt(tr.querySelector('th')),
        cells: $$('td', tr).map(function (td) {
          var i = td.querySelector('.u-in');
          var c = td.querySelector('.u-cm');
          if (!i && !c) return { in: txt(td), cm: txt(td) };
          return { in: i ? txt(i) : txt(c), cm: c ? txt(c) : txt(i) };
        })
      };
    });
    chartCache = rows.length ? { cols: cols, rows: rows, garment: isGarment } : false;
    return chartCache;
  }

  function drawMeasure() {
    var row = sizeRow();
    if (!row) return;
    var chart = chartTable();
    if (!chart) return;

    var box = row.querySelector('[data-pdp-measure]');
    if (!box) {
      box = document.createElement('div');
      box.className = 'pmeasure';
      box.setAttribute('data-pdp-measure', '');
      box.setAttribute('aria-live', 'polite');
      var values = $('.option__values', row);
      if (values && values.parentNode) values.parentNode.insertBefore(box, values.nextSibling);
      else row.appendChild(box);
    }

    var on = row.querySelector('input:checked');
    var size = on ? on.value : null;
    var rec = null;
    if (size) {
      for (var i = 0; i < chart.rows.length; i++) {
        if (chart.rows[i].size === String(size)) { rec = chart.rows[i]; break; }
      }
    }

    /* The button's label is the unit it switches TO, which is what a shopper
       reads it as. Screen readers get the sentence instead, because "cm" on
       its own, straight after "Size M fits", is not one. */
    var toggle = '<button type="button" class="pmeasure__unit" data-pdp-unit aria-label="' +
      (unit === 'in' ? 'Show measurements in centimetres' : 'Show measurements in inches') +
      '">' + (unit === 'in' ? 'cm' : 'in') + '</button>';

    if (!rec) {
      box.innerHTML = '<p class="pmeasure__head"><strong>' + esc(chart.cols[0] || 'Size') +
        '</strong>' + toggle + '</p>' +
        '<p class="pmeasure__empty">Pick a size to see its measurements.</p>';
      return;
    }

    var h = '<p class="pmeasure__head"><strong>' + esc(chart.cols[0] || 'Size') + ' ' +
      esc(rec.size) + '</strong> <span>' + (chart.garment ? 'measures' : 'fits') +
      '</span>' + toggle + '</p><dl class="pmeasure__list">';
    for (var c = 0; c < rec.cells.length; c++) {
      var label = chart.cols[c + 1] || '';
      h += '<div><dt>' + esc(label) + '</dt><dd>' +
        esc(unit === 'cm' ? rec.cells[c].cm : rec.cells[c].in) + '</dd></div>';
    }
    /* 21/10 — a nota tem de dizer o que a tabela lida realmente e. Quando o
       produto tem custom.size_table, a primeira .sc__table da gaveta e a da
       PECA medida no plano; a nota antiga dizia "medidas de corpo" nos dois
       casos, e nesse virava mentira na propria pagina. */
    h += '</dl><p class="pmeasure__note">' + (chart.garment
      ? 'The garment laid flat, not your body. Allow 1–2 cm either way.'
      : 'Body measurements in US sizing — the body this size is cut for, not ' +
        'the measured dimensions of the garment.') + '</p>';
    box.innerHTML = h;
  }

  /* --------------------------------------------------------------- buy it now */
  function addButton() {
    return $('vg-add-form [type="submit"][name="add"]');
  }

  function initBuyNow() {
    var form = $('vg-add-form form');
    if (!form) return;
    var buy = $('.product__buy', form);
    if (!buy || form.querySelector('[data-buy-now]')) return;

    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'button button--outline button--lg pbuy-now';
    b.setAttribute('data-buy-now', '');
    b.textContent = 'Buy it now';
    buy.parentNode.insertBefore(b, buy.nextSibling);
    syncBuyNow();
  }

  /* Availability lives on the real Add to bag button, which theme.js keeps
     correct. Mirror it rather than decide twice and risk disagreeing. */
  function syncBuyNow() {
    var b = $('[data-buy-now]');
    var add = addButton();
    if (!b || !add) return;
    if (add.getAttribute('aria-disabled') === 'true') b.setAttribute('aria-disabled', 'true');
    else b.removeAttribute('aria-disabled');
  }

  function buyNow(b) {
    if (b.getAttribute('aria-disabled') === 'true') return;
    var form = b.closest('form');
    if (!form) return;
    var id = form.querySelector('[name="id"]');
    var qty = form.querySelector('[name="quantity"]');
    if (!id || !id.value) return;

    var old = $('.pbuy-err', form.parentNode);
    if (old) old.remove();

    var label = b.textContent;
    b.setAttribute('aria-disabled', 'true');
    b.textContent = '…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(id.value), quantity: qty ? Number(qty.value) || 1 : 1 }]
      })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () { window.location.href = window.Shopify && window.Shopify.routes
        ? (window.Shopify.routes.root + 'checkout').replace('//', '/')
        : '/checkout'; })
      .catch(function () {
        b.removeAttribute('aria-disabled');
        b.textContent = label;
        var p = document.createElement('p');
        p.className = 'pbuy-err';
        p.setAttribute('role', 'status');
        p.textContent = 'Checkout did not start. Try Add to bag.';
        b.parentNode.insertBefore(p, b.nextSibling);
      });
  }

  /* --------------------------------------------------------------------- wire */
  function onOptionChange() {
    /* theme.js listens on <vg-variant-picker>; this one is on the document, so
       it runs after it in the bubble phase and sees the gallery as it left it. */
    promote(mediaFor(currentVariant()));
    drawMeasure();
    syncBuyNow();
  }

  function start() {
    if (!$('.product__info') && !$('.product__gallery')) return;
    styleOnce();
    initGallery();
    filterColour(chosenColour());
    initBuyNow();
    drawMeasure();

    document.addEventListener('change', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('vg-variant-picker')) { onOptionChange(); return; }
      /* The guide drawer's own in/cm switch, kept in step. */
      if (e.target.id === 'sc-in' || e.target.id === 'sc-cm') {
        unit = e.target.id === 'sc-cm' ? 'cm' : 'in';
        drawMeasure();
      }
    });

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var u = e.target.closest('[data-pdp-unit]');
      if (u) {
        unit = unit === 'in' ? 'cm' : 'in';
        var mirror = document.getElementById(unit === 'cm' ? 'sc-cm' : 'sc-in');
        if (mirror) mirror.checked = true;
        drawMeasure();
        return;
      }
      var b = e.target.closest('[data-buy-now]');
      if (b) { e.preventDefault(); buyNow(b); }
    });

    window.addEventListener('resize', function () {
      clearPromotion();
      promoted = null;
      markDots();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
